import useAxios from "axios-hooks";
import { useManualQuery } from "hooks/useManualQuery";
import useQueryParams from "hooks/useQueryParams";
import { SourceType } from "model";
import { useMemo } from "react";
import { formatUrl } from "utils";

import {
  CompanyParams,
  CreateConnectionParams,
  IntegrationParams,
  api,
} from "config/urls";

import { ApiError, ApiImage, ConfigResponse } from "./shared";
import { ParsedQuery } from "query-string";

export const useCompanyConfigQuery = ({
  companyId,
  queryParams,
}: CompanyParams & { queryParams: URLSearchParams }) => {
  return useAxios<ConfigResponse, ApiError>(
    {
      url: formatUrl<CompanyParams>(api.companies.config, {
        companyId,
      }),
      params: queryParams,
    },
    {
      manual: true,
    }
  );
};

export type SourceTypeResponse = {
  sourceType: SourceType;
  connected: number;
  disconnected: number;
  priority: number;
  integrationsUri: string;
  maxConnections?: number;
  displayWarning: boolean;
  useCustomIntro: boolean;
  displayRevisit: boolean;
  uploadedFiles?: number;
};

export type SourceTypesQueryResponse = SourceTypeResponse[];

export const useSourceTypesQuery = () => {
  return useManualQuery<SourceTypesQueryResponse, CompanyParams>(
    api.companies.integrations.sourceTypes
  );
};

export interface Integration {
  key: string;
  logoUrl?: string;
  logo: ApiLogo;
  name: string;
  clientLimitReached: boolean;
  sourceType: SourceType;
  priority: number;
  alternativeNames?: string[];
}

export interface ApiLogo {
  full?: { image: ApiImage };
  square?: { image: ApiImage };
}

export interface ButtonImage {
  default: { image: ApiImage };
  hover: { image: ApiImage };
}
export interface IntegrationBranding extends Integration {
  logo: { full: { image: ApiImage }; square: { image: ApiImage } };
  button: ButtonImage;
}

export interface IntegrationsResponse {
  results: Integration[];
  pageNumber: number;
  pageSize: number;
  totalResults: number;
  _links: {
    current: {
      href: string;
    };
    self: {
      href: string;
    };
  };
}

export interface ConnectionResponse {
  linkUrl: string;
}

export interface DisplayTextResponse {
  name?: string;
  errorMessage?: string;
  correlationId?: string;
  platformKey?: string;
  message?: string;
}

export const useIntegrationsQuery = (integrationsUri: string) => {
  const queryParams = useQueryParams();
  const showSandboxIntegrations = useMemo(
    () => queryParams.get("link.showSandboxIntegrations"),
    [queryParams]
  );
  return useAxios<IntegrationsResponse, ApiError>({
    url: integrationsUri,
    params: { "link.showSandboxIntegrations": showSandboxIntegrations },
  });
};

export const useManualIntegrationsQuery = (integrationsUri: string) => {
  const queryParams = useQueryParams();
  const showSandboxIntegrations = useMemo(
    () => queryParams.get("link.showSandboxIntegrations"),
    [queryParams]
  );
  return useAxios<IntegrationsResponse, ApiError>(
    {
      url: integrationsUri,
      params: { "link.showSandboxIntegrations": showSandboxIntegrations },
    },
    { manual: true }
  );
};

export const useIntegrationQuery = ({
  companyId,
  platformKey,
}: IntegrationParams) => {
  return useAxios<IntegrationBranding, ApiError>({
    url: formatUrl<IntegrationParams>(api.companies.integrations.platform, {
      companyId,
      platformKey,
    }),
    method: "GET",
  });
};

export const useManualIntegrationQuery = () => {
  return useManualQuery<IntegrationBranding, IntegrationParams>(
    api.companies.integrations.platform
  );
};

export const useCreateConnectionQuery = ({
  companyId,
  platformKey,
  query,
}: CreateConnectionParams) => {
  return useAxios<ConnectionResponse, { platformKey: string }, ApiError>(
    {
      url: `${formatUrl<CompanyParams>(api.companies.createConnection, {
        companyId,
      })}${query}`,
      method: "POST",
      data: {
        platformKey,
      },
    },
    { manual: true }
  );
};

export const useCompanyRedirectValidateQuery = ({
  companyId,
  redirectUrl,
}: CompanyParams & { redirectUrl: string }) => {
  return useAxios<ConfigResponse, ApiError>(
    {
      url: formatUrl<CompanyParams>(
        api.companies.redirectValidate,
        {
          companyId,
        },
        { redirectUrl }
      ),
    },
    {
      manual: true,
    }
  );
};

export type CustomRedirectResponse =
  | {
      hasCustomRedirect: true;
      customRedirectUrl: string;
    }
  | {
      hasCustomRedirect: false;
    };

export const useGetCustomRedirect = ({
  companyId,
  params,
}: {
  companyId: string;
  params: ParsedQuery;
}) => {
  return useAxios<CustomRedirectResponse, ApiError>(
    {
      url: formatUrl<CompanyParams>(
        api.companies.customRedirect,
        {
          companyId,
        },
        params
      ),
    },
    { manual: true }
  );
};

export const useDisplayTextQuery = ({ companyId }: CompanyParams) => {
  return useAxios<DisplayTextResponse, ApiError>({
    url: formatUrl<CompanyParams>(api.companies.displayText, {
      companyId,
    }),
  });
};
